<template>
  <div class="analysis-engine-page" :class="{ 'analysis-engine-page-rtl': isRtl === true }" :dir="isRtl === true ? 'rtl' : 'ltr'">
    <div class="analysis-engine-header">
      <div class="analysis-engine-header-intro">
        <h1 class="text-2xl text-gray-900 dark:text-gray-200 font-medium">{{ t('analysisEngine.title', 'Analysis Engine') }}</h1>
        <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">{{ t('analysisEngine.subtitle', 'Choose what each agent or entity analyzes after a conversation closes and add custom prompt-based analysis fields.') }}</p>
      </div>
      <button class="btn bg-indigo-600 hover:bg-indigo-700 text-white" :disabled="saveDisabled === true" @click="saveAnalysisEngine">
        <span v-if="isSaving === true" class="loading loading-spinner loading-xs analysis-save-spinner"></span>
        {{ t('analysisEngine.saveButton', 'Save') }}
      </button>
    </div>

    <div class="analysis-card bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-md shadow p-4">
      <div class="analysis-controls">
        <div class="form-control analysis-target-type-select">
          <label class="label">
            <span class="label-text font-medium">{{ t('analysisEngine.targetTypeLabel', 'Configure for') }}</span>
          </label>
          <select v-model="targetType" class="select select-bordered w-full">
            <option value="agents">{{ t('analysisEngine.targetTypeAgents', 'AI agents') }}</option>
            <option value="profiles">{{ t('analysisEngine.targetTypeProfiles', 'Analysis profiles') }}</option>
          </select>
        </div>
        <div v-if="targetType === 'agents'" class="form-control analysis-agent-select">
          <MultiSelect
            v-model="selectedAgentIds"
            :options="agents"
            :label="t('analysisEngine.agentLabel', 'Agent')"
            :placeholder="t('analysisEngine.selectAgentPlaceholder', 'Select agent')"
            id-field="agentid"
            label-field="agentname"
            :is-loading="isLoadingAgents === true"
            :max-selections="1"
            :force-direction="isRtl === true ? 'rtl' : 'ltr'"
          />
        </div>
        <div v-if="targetType === 'profiles'" class="form-control analysis-profile-select">
          <label class="label">
            <span class="label-text font-medium">{{ t('analysisEngine.analysisProfileLabel', 'Analysis profile') }}</span>
          </label>
          <select v-model="selectedAnalysisProfileId" class="select select-bordered w-full" :disabled="isLoadingProfiles === true">
            <option value="">{{ t('analysisEngine.selectAnalysisProfileOption', 'Select profile') }}</option>
            <option v-for="profile in analysisProfileOptions" :key="profile.profileId" :value="profile.profileId">{{ profile.displayName }}</option>
          </select>
        </div>
        <div v-if="targetType === 'profiles'" class="form-control analysis-profile-create">
          <button class="btn btn-outline btn-info analysis-profile-create-button" type="button" :disabled="isSaving === true" @click="openCreateProfileModal">{{ t('analysisEngine.createProfileButton', 'Create profile') }}</button>
        </div>
        <div v-if="targetType === 'profiles'" class="form-control analysis-profile-entities">
          <MultiSelect
            v-model="profileEntityIds"
            :options="entityOptions"
            :label="t('analysisEngine.profileEntitiesLabel', 'Attached entities')"
            :placeholder="t('analysisEngine.profileEntitiesPlaceholder', 'Select entities affected by this profile')"
            id-field="entityId"
            label-field="displayName"
            :is-loading="isLoadingEntities === true"
            :force-direction="isRtl === true ? 'rtl' : 'ltr'"
          />
        </div>
      </div>

      <div class="analysis-enable-panel dark:bg-gray-900 dark:border-gray-700" :class="{ 'analysis-enable-panel-disabled': hasSelectedTarget === false, 'analysis-enable-panel-off': engineEnabled === false && hasSelectedTarget === true }">
        <div class="form-control">
          <label class="label cursor-pointer justify-between analysis-engine-toggle">
            <span class="label-text analysis-engine-toggle-text text-gray-900 dark:text-gray-100">{{ runAnalysisLabel }}</span>
            <input v-model="engineEnabled" type="checkbox" class="toggle toggle-primary" :disabled="hasSelectedTarget === false" />
          </label>
          <p v-if="hasSelectedTarget === false" class="analysis-enable-hint">{{ selectTargetHint }}</p>
          <p v-else-if="engineEnabled === false" class="analysis-enable-hint">{{ t('analysisEngine.enableHintOff', 'Analysis is off. Turn on to configure built-in and custom fields.') }}</p>
        </div>
      </div>

      <div v-if="isBusyLoading === true" class="analysis-loader">
        <span class="loading loading-spinner loading-lg text-indigo-600"></span>
      </div>

      <div v-else-if="hasSelectedTarget === false" class="analysis-empty">
        {{ emptySelectTargetMessage }}
      </div>

      <div v-else>
        <div class="analysis-section">
          <div class="analysis-section-title-row">
            <div class="analysis-section-heading">
              <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100">{{ t('analysisEngine.builtInTitle', 'Built-in analysis') }}</h2>
              <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">{{ t('analysisEngine.builtInSubtitle', 'These are the current conversation analysis fields already supported by reports.') }}</p>
            </div>
            <div class="analysis-buttons-row analysis-section-actions">
              <button class="btn btn-sm btn-outline" type="button" @click="selectAllStandardItems">{{ t('analysisEngine.selectAllButton', 'Select all') }}</button>
              <button class="btn btn-sm btn-outline" type="button" @click="clearStandardItems">{{ t('analysisEngine.clearButton', 'Clear') }}</button>
            </div>
          </div>

          <div class="analysis-items-grid">
            <div v-for="item in standardAnalysisItems" :key="item.key" class="analysis-item-card cursor-pointer dark:bg-gray-900 dark:border-gray-700" @click="toggleStandardItem(item.key)">
              <input v-model="selectedStandardItems" type="checkbox" class="checkbox checkbox-primary analysis-item-checkbox" :value="item.key" :disabled="engineEnabled === false" @click.stop />
              <div class="analysis-item-copy">
                <div class="analysis-item-title-row">
                  <div class="font-semibold text-gray-900 dark:text-gray-100">{{ item.label }}</div>
                  <span v-if="hasStandardPromptOverride(item.key) === true" class="badge badge-sm badge-info">{{ t('analysisEngine.customPromptBadge', 'Custom prompt') }}</span>
                  <span v-if="isStandardDetailedExplanationEnabled(item.key) === true" class="badge badge-sm badge-accent">{{ t('analysisEngine.detailedExplanationBadge', 'Explains results') }}</span>
                </div>
                <div class="analysis-standard-prompt-row">
                  <button v-if="canEditStandardPrompt(item) === true" type="button" class="analysis-standard-prompt-edit" :disabled="engineEnabled === false" @click.stop.prevent="openStandardPromptModal(item)">
                    <Icon icon="tabler:settings-edit" width="14" />
                    <span>{{ t('analysisEngine.editSystemPromptButton', 'Edit analysis') }}</span>
                  </button>
                  <span v-else class="analysis-standard-prompt-placeholder"></span>
                </div>
                <label class="analysis-detailed-explanation-toggle" @click.stop>
                  <input type="checkbox" class="toggle toggle-xs toggle-accent" :checked="isStandardDetailedExplanationEnabled(item.key) === true" :disabled="engineEnabled === false || selectedStandardItems.includes(item.key) === false" @change="setStandardDetailedExplanation(item.key, $event)" />
                  <span>{{ t('analysisEngine.detailedExplanationToggle', 'Explain each result') }}</span>
                </label>
                <div class="text-xs text-gray-500 dark:text-gray-400 mt-1">{{ item.description }}</div>
              </div>
              <div class="analysis-item-tooltip-shell" @click.stop>
                <ToolTip class="analysis-item-tooltip" :position="isRtl === true ? 'left' : 'right'">
                  <h2 class="card-title text-sm">{{ getStandardProcessingTooltipTitle(item) }}</h2>
                  <p class="text-xs">{{ getStandardProcessingTooltipBody(item) }}</p>
                </ToolTip>
              </div>
            </div>
          </div>
        </div>

        <div v-if="targetType === 'profiles'" class="analysis-section">
          <div class="analysis-section-title-row">
            <div class="analysis-section-heading">
              <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100">{{ t('analysisEngine.tagCollectionsTitle', 'Tag collections') }}</h2>
              <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">{{ t('analysisEngine.tagCollectionsSubtitle', 'Connect this profile to the tag collections that the "Identified tags" analysis should use.') }}</p>
            </div>
          </div>

          <div v-if="isLoadingAnalysisCollections === true" class="text-center py-6">
            <span class="loading loading-spinner loading-lg text-indigo-600"></span>
          </div>
          <div v-else-if="availableAnalysisCollections.length === 0" class="analysis-empty-small">
            {{ t('analysisEngine.tagCollectionsEmpty', 'No tag collections found. Create one in Tags Manager to use tag analysis in this profile.') }}
          </div>
          <div v-else class="form-control">
            <MultiSelect
              v-model="selectedAnalysisCollectionIds"
              :options="availableAnalysisCollections"
              :label="t('analysisEngine.tagCollectionsLabel', 'Tag collections')"
              :placeholder="t('analysisEngine.tagCollectionsPlaceholder', 'Select tag collections')"
              id-field="collectionId"
              label-field="collectionName"
              :disabled="engineEnabled === false"
              :force-direction="isRtl === true ? 'rtl' : 'ltr'"
            />
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-2">{{ t('analysisEngine.tagCollectionsInfo', 'These collections are used when "Identified tags" is enabled in Built-in analysis.') }}</p>
            <p v-if="selectedStandardItems.includes('identifiedTags') === false" class="text-xs text-warning mt-2">{{ t('analysisEngine.tagCollectionsDisabledHint', 'Turn on "Identified tags" above to use the selected collections during analysis.') }}</p>
          </div>
        </div>

        <div class="analysis-section">
          <div class="analysis-section-title-row">
            <div class="analysis-section-heading">
              <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100">{{ t('analysisEngine.customTitle', 'Custom prompt analysis') }}</h2>
              <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">{{ t('analysisEngine.customSubtitle', 'Add fields that the LLM should answer from a prompt for every conversation.') }}</p>
            </div>
            <div class="analysis-section-actions">
              <button class="btn btn-sm btn-outline btn-info" type="button" :disabled="engineEnabled === false" @click="openAddCustomItemModal">{{ t('analysisEngine.addItemButton', 'Add item') }}</button>
            </div>
          </div>

          <div v-if="customItems.length === 0" class="analysis-empty-small">{{ t('analysisEngine.noCustomItems', 'No custom analysis items yet.') }}</div>

          <div v-for="(item, index) in customItems" :key="item.localId" class="custom-analysis-item bg-base-100 dark:bg-gray-900 border border-base-300 dark:border-gray-700 rounded-md p-4">
            <div class="custom-analysis-display-row">
              <div class="custom-analysis-display-copy">
                <div class="custom-analysis-title-row">
                  <div class="font-semibold text-gray-900 dark:text-gray-100">{{ item.name !== '' ? item.name : item.key }}</div>
                  <div class="analysis-item-tooltip-shell" @click.stop>
                    <ToolTip class="analysis-item-tooltip" :position="isRtl === true ? 'left' : 'right'">
                      <h2 class="card-title text-sm">{{ getCustomProcessingTooltipTitle(item) }}</h2>
                      <p class="text-xs">{{ getCustomProcessingTooltipBody(item) }}</p>
                    </ToolTip>
                  </div>
                </div>
                <div class="text-xs text-gray-500 dark:text-gray-400 mt-1">{{ t('analysisEngine.itemMeta', { key: item.key, output: getOutputTypeLabel(item.outputType) }, 'Key: {key} · Output: {output}') }}</div>
                <div v-if="item.includeDetailedExplanation === true" class="analysis-detailed-explanation-status">
                  <Icon icon="tabler:info-circle" width="13" />
                  <span>{{ t('analysisEngine.detailedExplanationEnabledText', 'Detailed result explanations enabled') }}</span>
                </div>
              </div>
              <div class="analysis-buttons-row">
                <button class="btn btn-sm btn-outline" type="button" :disabled="engineEnabled === false" @click="openEditCustomItemModal(index)">{{ t('analysisEngine.editButton', 'Edit') }}</button>
                <button class="btn btn-sm btn-outline btn-error" type="button" @click="removeCustomItem(index)">{{ t('analysisEngine.removeButton', 'Remove') }}</button>
              </div>
            </div>
            <p class="text-sm text-gray-600 dark:text-gray-300 mt-3 whitespace-pre-wrap">{{ item.prompt }}</p>
          </div>
        </div>
      </div>
    </div>

    <AppModal :visible="customItemModalOpen === true" :title="customItemModalTitle" boxClass="max-w-2xl w-11/12" @close="closeCustomItemModal">
      <div class="p-2 analysis-modal-content" :class="{ 'analysis-modal-content-rtl': isRtl === true }" :dir="isRtl === true ? 'rtl' : 'ltr'">
        <div class="custom-analysis-modal-grid">
          <div class="form-control">
            <label class="label">
              <span class="label-text font-medium">{{ t('analysisEngine.form.nameLabel', 'Name') }}</span>
            </label>
            <input v-model="customItemDraft.name" class="input input-bordered w-full" maxlength="80" :placeholder="t('analysisEngine.form.namePlaceholder', 'Sales risk')" />
          </div>
          <div class="form-control">
            <label class="label">
              <span class="label-text font-medium">{{ t('analysisEngine.form.keyLabel', 'Key') }}</span>
            </label>
            <input v-model="customItemDraft.key" class="input input-bordered w-full" maxlength="80" :placeholder="t('analysisEngine.form.keyPlaceholder', 'salesRisk')" />
          </div>
          <div class="form-control">
            <label class="label">
              <span class="label-text font-medium">{{ t('analysisEngine.form.outputTypeLabel', 'Output type') }}</span>
            </label>
            <select v-model="customItemDraft.outputType" class="select select-bordered w-full">
              <option value="text">{{ t('analysisEngine.outputTypes.text', 'Text') }}</option>
              <option value="boolean">{{ t('analysisEngine.outputTypes.boolean', 'Boolean') }}</option>
              <option value="number">{{ t('analysisEngine.outputTypes.number', 'Number') }}</option>
              <option value="object">{{ t('analysisEngine.outputTypes.object', 'Object') }}</option>
              <option value="array">{{ t('analysisEngine.outputTypes.array', 'Array') }}</option>
            </select>
          </div>
        </div>
        <div class="form-control mt-3">
          <label class="label">
            <span class="label-text font-medium">{{ t('analysisEngine.form.promptLabel', 'Prompt') }}</span>
          </label>
          <textarea v-model="customItemDraft.prompt" class="textarea textarea-bordered w-full" rows="5" maxlength="1200" :placeholder="t('analysisEngine.form.promptPlaceholder', 'Decide if this conversation shows a risk of losing the customer.')"></textarea>
        </div>
        <label class="analysis-detailed-explanation-modal-toggle">
          <input v-model="customItemDraft.includeDetailedExplanation" type="checkbox" class="toggle toggle-accent" />
          <span>
            <strong>{{ t('analysisEngine.detailedExplanationToggle', 'Explain each result') }}</strong>
            <small>{{ t('analysisEngine.detailedExplanationHelp', 'Ask the analysis to store a short evidence-based reason for every result. It appears as a report tooltip.') }}</small>
          </span>
        </label>
      </div>
      <template #actions>
        <div class="analysis-modal-actions" :class="{ 'analysis-modal-actions-rtl': isRtl === true }">
          <button type="button" class="btn btn-ghost" @click="closeCustomItemModal">{{ t('analysisEngine.cancelButton', 'Cancel') }}</button>
          <button type="button" class="btn bg-indigo-600 hover:bg-indigo-700 text-white" @click="saveCustomItemFromModal">{{ t('analysisEngine.applyButton', 'Apply') }}</button>
        </div>
      </template>
    </AppModal>

    <AppModal :visible="standardPromptModalOpen === true" :title="standardPromptModalTitle" boxClass="max-w-2xl w-11/12" @close="closeStandardPromptModal">
      <div class="p-2 analysis-modal-content" :class="{ 'analysis-modal-content-rtl': isRtl === true }" :dir="isRtl === true ? 'rtl' : 'ltr'">
        <p class="text-sm text-gray-500 dark:text-gray-400">{{ t('analysisEngine.standardPromptHelp', 'Leave this empty to use the built-in system analysis.') }}</p>
        <div class="form-control mt-3">
          <label class="label">
            <span class="label-text font-medium">{{ t('analysisEngine.form.promptLabel', 'Prompt') }}</span>
          </label>
          <textarea v-model="standardPromptDraft" class="textarea textarea-bordered w-full" rows="6" maxlength="1500" :placeholder="t('analysisEngine.standardPromptPlaceholder', 'Use the default system analysis unless you write a custom one here.')"></textarea>
        </div>
      </div>
      <template #actions>
        <div class="analysis-modal-actions" :class="{ 'analysis-modal-actions-rtl': isRtl === true }">
          <button type="button" class="btn btn-ghost" @click="closeStandardPromptModal">{{ t('analysisEngine.cancelButton', 'Cancel') }}</button>
          <button type="button" class="btn btn-outline" @click="clearStandardPromptDraft">{{ t('analysisEngine.useSystemPromptButton', 'Use system analysis') }}</button>
          <button type="button" class="btn bg-indigo-600 hover:bg-indigo-700 text-white" :disabled="isSaving === true" @click="saveStandardPromptFromModal">{{ t('analysisEngine.savePromptButton', 'Save analysis') }}</button>
        </div>
      </template>
    </AppModal>

    <AppModal :visible="createProfileModalOpen === true" :title="t('analysisEngine.createProfileButton', 'Create profile')" boxClass="max-w-xl w-11/12" @close="closeCreateProfileModal">
      <div class="p-2 analysis-modal-content" :class="{ 'analysis-modal-content-rtl': isRtl === true }" :dir="isRtl === true ? 'rtl' : 'ltr'">
        <div class="form-control">
          <label class="label">
            <span class="label-text font-medium">{{ t('analysisEngine.newAnalysisProfileLabel', 'New profile name') }}</span>
          </label>
          <input v-model="newAnalysisProfileNameDraft" class="input input-bordered w-full" maxlength="80" :placeholder="t('analysisEngine.newAnalysisProfilePlaceholder', 'Service quality profile')" @keyup.enter="createAnalysisProfile" />
        </div>
      </div>
      <template #actions>
        <div class="analysis-modal-actions" :class="{ 'analysis-modal-actions-rtl': isRtl === true }">
          <button type="button" class="btn btn-ghost" @click="closeCreateProfileModal">{{ t('analysisEngine.cancelButton', 'Cancel') }}</button>
          <button type="button" class="btn bg-indigo-600 hover:bg-indigo-700 text-white" :disabled="isSaving === true" @click="createAnalysisProfile">
            <span v-if="isSaving === true" class="loading loading-spinner loading-xs analysis-save-spinner"></span>
            {{ t('analysisEngine.createProfileButton', 'Create profile') }}
          </button>
        </div>
      </template>
    </AppModal>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import axios from '@/api/axiosInstance';
import { useToast } from 'vue-toastification';
import { Icon } from '@iconify/vue/dist/offline';
import { globalState } from '@/globalState';
import { getAllAIChats, invalidateAllAIChatsCache } from '@/shared/services/aiChatsService';
import AppModal from '@/components/AppModal.vue';
import MultiSelect from '@/components/MultiSelect.vue';
import ToolTip from '@/components/Tooltip.vue';
import { useI18n } from '@/i18n';

const { t, currentLanguage } = useI18n();
const route = useRoute();
const toast = useToast();
const targetType = ref('agents');
const agents = ref([]);
const entities = ref([]);
const analysisProfiles = ref([]);
const selectedAgentIds = ref([]);
const selectedAnalysisProfileId = ref('');
const profileEntityIds = ref([]);
const selectedAgent = ref(null);
const isLoadingAgents = ref(false);
const isLoadingEntities = ref(false);
const isLoadingProfiles = ref(false);
const isLoadingAgent = ref(false);
const isSaving = ref(false);
const isApplyingAnalysisProfile = ref(false);
const engineEnabled = ref(true);
const analysisProfileNameDraft = ref('');
const newAnalysisProfileNameDraft = ref('');
const createProfileModalOpen = ref(false);
const availableAnalysisCollections = ref([]);
const isLoadingAnalysisCollections = ref(false);
const selectedAnalysisCollectionIds = ref([]);
const selectedStandardItems = ref([]);
const standardPromptOverrides = ref({});
const standardPromptModalOpen = ref(false);
const standardPromptKey = ref('');
const standardPromptDraft = ref('');
const standardDetailedExplanations = ref({});
const customItems = ref([]);
const customItemModalOpen = ref(false);
const customItemEditIndex = ref(-1);
const customItemDraft = ref({
  key: '',
  name: '',
  prompt: '',
  outputType: 'text',
  includeDetailedExplanation: false
});

const standardAnalysisItems = computed(() => [
  { key: 'languageDetected', label: t('analysisEngine.standardItems.languageDetected.label', 'Language detected'), description: t('analysisEngine.standardItems.languageDetected.description', 'Primary language used in the conversation.') },
  { key: 'summary', label: t('analysisEngine.standardItems.summary.label', 'Summary'), description: t('analysisEngine.standardItems.summary.description', 'Short summary in the conversation language.') },
  { key: 'gender', label: t('analysisEngine.standardItems.gender.label', 'Gender detection'), description: t('analysisEngine.standardItems.gender.description', 'Detected user gender and confidence.') },
  { key: 'conversationSubject', label: t('analysisEngine.standardItems.conversationSubject.label', 'Conversation subject'), description: t('analysisEngine.standardItems.conversationSubject.description', 'Main subject of the conversation.') },
  { key: 'isConversationFollowUp', label: t('analysisEngine.standardItems.isConversationFollowUp.label', 'Follow-up detection'), description: t('analysisEngine.standardItems.isConversationFollowUp.description', 'Whether this looks like a follow-up interaction.') },
  { key: 'conversationType', label: t('analysisEngine.standardItems.conversationType.label', 'Conversation type'), description: t('analysisEngine.standardItems.conversationType.description', 'Sales, service, customer service, or general questions.') },
  { key: 'crs', label: t('analysisEngine.standardItems.crs.label', 'Customer resolution status'), description: t('analysisEngine.standardItems.crs.description', 'Whether the agent resolved the issue.') },
  { key: 'nps', label: t('analysisEngine.standardItems.nps.label', 'Predicted CSAT likelihood'), description: t('analysisEngine.standardItems.nps.description', 'Estimated likelihood the customer would return.') },
  { key: 'curses', label: t('analysisEngine.standardItems.curses.label', 'Curses detection'), description: t('analysisEngine.standardItems.curses.description', 'Whether curses were detected and which words were found.') },
  { key: 'unansweredQuestions', label: t('analysisEngine.standardItems.unansweredQuestions.label', 'Unanswered questions'), description: t('analysisEngine.standardItems.unansweredQuestions.description', 'Questions the agent did not answer adequately.') },
  { key: 'overallSentiment', label: t('analysisEngine.standardItems.overallSentiment.label', 'Overall sentiment'), description: t('analysisEngine.standardItems.overallSentiment.description', 'Conversation-level sentiment label.') },
  { key: 'overallSentimentScores', label: t('analysisEngine.standardItems.overallSentimentScores.label', 'Overall sentiment scores'), description: t('analysisEngine.standardItems.overallSentimentScores.description', 'Positive, neutral, and negative scores.') },
  { key: 'participantsSentiments', label: t('analysisEngine.standardItems.participantsSentiments.label', 'Participant sentiments'), description: t('analysisEngine.standardItems.participantsSentiments.description', 'User and agent sentiment details.') },
  { key: 'isChatAbandoned', supportsPromptEdit: true, label: t('analysisEngine.standardItems.isChatAbandoned.label', 'Chat abandoned'), description: t('analysisEngine.standardItems.isChatAbandoned.description', 'Whether the user left before a natural conclusion.') },
  { key: 'salesDataReliabilityScore', label: t('analysisEngine.standardItems.salesDataReliabilityScore.label', 'Sales data reliability score'), description: t('analysisEngine.standardItems.salesDataReliabilityScore.description', 'Scores data reliability in sales conversations.') },
  { key: 'salesCustomerGuidanceScore', label: t('analysisEngine.standardItems.salesCustomerGuidanceScore.label', 'Sales customer guidance score'), description: t('analysisEngine.standardItems.salesCustomerGuidanceScore.description', 'Scores how well the customer was guided through the sales process.') },
  { key: 'salesEmpathySupportScore', label: t('analysisEngine.standardItems.salesEmpathySupportScore.label', 'Sales empathy and support score'), description: t('analysisEngine.standardItems.salesEmpathySupportScore.description', 'Scores politeness, patience, empathy, listening, and support in sales.') },
  { key: 'salesOfferFitScore', label: t('analysisEngine.standardItems.salesOfferFitScore.label', 'Sales offer fit score'), description: t('analysisEngine.standardItems.salesOfferFitScore.description', 'Scores how well the offer fit the customer needs.') },
  { key: 'salesDifficultCaseHandlingScore', label: t('analysisEngine.standardItems.salesDifficultCaseHandlingScore.label', 'Sales difficult case handling score'), description: t('analysisEngine.standardItems.salesDifficultCaseHandlingScore.description', 'Scores handling of difficult sales cases and objections.') },
  { key: 'salesProfessionalismScore', label: t('analysisEngine.standardItems.salesProfessionalismScore.label', 'Sales professionalism score'), description: t('analysisEngine.standardItems.salesProfessionalismScore.description', 'Scores professionalism in sales conversations.') },
  { key: 'salesCrmDocumentationScore', label: t('analysisEngine.standardItems.salesCrmDocumentationScore.label', 'Sales CRM documentation score'), description: t('analysisEngine.standardItems.salesCrmDocumentationScore.description', 'Scores CRM documentation and details for continued work.') },
  { key: 'salesDealClosureScore', label: t('analysisEngine.standardItems.salesDealClosureScore.label', 'Sales closure score'), description: t('analysisEngine.standardItems.salesDealClosureScore.description', 'Scores actual sales call closure and next-step closure.') },
  { key: 'salesScorecardSummary', label: t('analysisEngine.standardItems.salesScorecardSummary.label', 'Sales scorecard summary'), description: t('analysisEngine.standardItems.salesScorecardSummary.description', 'Summarizes the sales scorecard decision.') },
  { key: 'serviceDataReliabilityScore', label: t('analysisEngine.standardItems.serviceDataReliabilityScore.label', 'Service data reliability score'), description: t('analysisEngine.standardItems.serviceDataReliabilityScore.description', 'Scores data reliability in service conversations.') },
  { key: 'serviceEmpathySupportScore', label: t('analysisEngine.standardItems.serviceEmpathySupportScore.label', 'Service empathy and support score'), description: t('analysisEngine.standardItems.serviceEmpathySupportScore.description', 'Scores politeness, patience, empathy, listening, and support in service.') },
  { key: 'servicePolicyFileOrientationScore', label: t('analysisEngine.standardItems.servicePolicyFileOrientationScore.label', 'Service file orientation score'), description: t('analysisEngine.standardItems.servicePolicyFileOrientationScore.description', 'Scores orientation in the insured or customer file.') },
  { key: 'serviceKeyIssueAttentionScore', label: t('analysisEngine.standardItems.serviceKeyIssueAttentionScore.label', 'Service key issue attention score'), description: t('analysisEngine.standardItems.serviceKeyIssueAttentionScore.description', 'Scores attention to important points and required follow-up.') },
  { key: 'serviceProfessionalismScore', label: t('analysisEngine.standardItems.serviceProfessionalismScore.label', 'Service professionalism score'), description: t('analysisEngine.standardItems.serviceProfessionalismScore.description', 'Scores professionalism in service conversations.') },
  { key: 'serviceCrmDocumentationScore', label: t('analysisEngine.standardItems.serviceCrmDocumentationScore.label', 'Service CRM documentation score'), description: t('analysisEngine.standardItems.serviceCrmDocumentationScore.description', 'Scores CRM documentation and details for continued handling.') },
  { key: 'serviceTreatmentClosureScore', label: t('analysisEngine.standardItems.serviceTreatmentClosureScore.label', 'Service treatment closure score'), description: t('analysisEngine.standardItems.serviceTreatmentClosureScore.description', 'Scores treatment loop closure and next-step clarity.') },
  { key: 'serviceScorecardSummary', label: t('analysisEngine.standardItems.serviceScorecardSummary.label', 'Service scorecard summary'), description: t('analysisEngine.standardItems.serviceScorecardSummary.description', 'Summarizes the service scorecard decision.') },
  { key: 'identifiedTags', label: t('analysisEngine.standardItems.identifiedTags.label', 'Identified tags'), description: t('analysisEngine.standardItems.identifiedTags.description', 'Relevant tags from the selected analysis tag collections.') }
]);

const isRtl = computed(() => {
  if (currentLanguage.value === 'he') {
    return true;
  }
  if (currentLanguage.value === 'ar') {
    return true;
  }
  return false;
});

function normalizeProfileIdValue(value) {
  if (value === null) {
    return '';
  }
  if (value === undefined) {
    return '';
  }
  return String(value).trim();
}

function normalizeAnalysisCollectionId(value) {
  if (typeof value === 'number' && Number.isFinite(value) === true) {
    return value;
  }
  if (typeof value === 'string') {
    const trimmedValue = value.trim();
    if (trimmedValue === '') {
      return '';
    }
    const parsedValue = Number.parseInt(trimmedValue, 10);
    if (Number.isNaN(parsedValue) === false && String(parsedValue) === trimmedValue) {
      return parsedValue;
    }
    return trimmedValue;
  }
  return '';
}

function normalizeAnalysisCollectionIds(value) {
  const rawItems = Array.isArray(value) === true ? value : [];
  const normalizedItems = [];
  for (let index = 0; index < rawItems.length; index = index + 1) {
    const normalizedId = normalizeAnalysisCollectionId(rawItems[index]);
    if (normalizedId === '') {
      continue;
    }
    if (normalizedItems.some((item) => String(item) === String(normalizedId)) === true) {
      continue;
    }
    normalizedItems.push(normalizedId);
  }
  return normalizedItems;
}

function normalizeAnalysisCollectionRow(itemParam) {
  const item = itemParam !== undefined && itemParam !== null && typeof itemParam === 'object' ? itemParam : {};
  const collectionId = normalizeAnalysisCollectionId(item.collectionId !== undefined ? item.collectionId : (item.collectionid !== undefined ? item.collectionid : item.id));
  if (collectionId === '') {
    return null;
  }
  const collectionNameRaw = item.collectionName !== undefined ? item.collectionName : (item.collectionname !== undefined ? item.collectionname : item.name);
  const collectionName = typeof collectionNameRaw === 'string' && collectionNameRaw.trim() !== '' ? collectionNameRaw.trim() : String(collectionId);
  return {
    ...item,
    collectionId,
    collectionName
  };
}

function syncSelectedAnalysisCollectionIds(value) {
  const normalizedIds = normalizeAnalysisCollectionIds(value);
  if (availableAnalysisCollections.value.length === 0) {
    selectedAnalysisCollectionIds.value = normalizedIds;
    return;
  }
  selectedAnalysisCollectionIds.value = normalizedIds.filter((id) =>
    availableAnalysisCollections.value.some((collection) => String(collection.collectionId) === String(id))
  );
}

const entityOptions = computed(() => {
  const languageCode = currentLanguage.value === 'he' ? 'he' : 'en';
  const options = [];
  const sourceEntities = Array.isArray(entities.value) === true ? entities.value : [];
  for (let index = 0; index < sourceEntities.length; index = index + 1) {
    const entity = sourceEntities[index];
    if (entity === null) {
      continue;
    }
    if (entity === undefined) {
      continue;
    }
    if (typeof entity !== 'object') {
      continue;
    }
    const entityId = typeof entity.entityId === 'string' ? entity.entityId.trim() : '';
    if (entityId === '') {
      continue;
    }
    let displayName = '';
    if (entity.name !== undefined && entity.name !== null && typeof entity.name === 'object') {
      if (typeof entity.name[languageCode] === 'string' && entity.name[languageCode].trim() !== '') {
        displayName = entity.name[languageCode].trim();
      } else if (typeof entity.name.en === 'string' && entity.name.en.trim() !== '') {
        displayName = entity.name.en.trim();
      }
    }
    if (displayName === '') {
      displayName = typeof entity.entityType === 'string' ? entity.entityType : entityId;
    }
    const entityTypeLabel = entity.typeLabels !== undefined && entity.typeLabels !== null && typeof entity.typeLabels[languageCode] === 'string'
      ? entity.typeLabels[languageCode].trim()
      : (typeof entity.entityType === 'string' ? entity.entityType : '');
    if (entityTypeLabel !== '' && entityTypeLabel !== displayName) {
      displayName = `${displayName} (${entityTypeLabel})`;
    }
    options.push({
      entityId: entityId,
      displayName: displayName
    });
  }
  return options;
});

const analysisProfileOptions = computed(() => {
  const languageCode = currentLanguage.value === 'he' ? 'he' : 'en';
  const options = [];
  const profiles = Array.isArray(analysisProfiles.value) === true ? analysisProfiles.value : [];
  for (let index = 0; index < profiles.length; index = index + 1) {
    const profile = profiles[index];
    if (profile === null) {
      continue;
    }
    if (profile === undefined) {
      continue;
    }
    if (typeof profile !== 'object') {
      continue;
    }
    const profileId = normalizeProfileIdValue(profile.profileId);
    if (profileId === '') {
      continue;
    }
    let displayName = '';
    if (profile.name !== undefined && profile.name !== null && typeof profile.name === 'object') {
      if (typeof profile.name[languageCode] === 'string' && profile.name[languageCode].trim() !== '') {
        displayName = profile.name[languageCode].trim();
      } else if (typeof profile.name.en === 'string' && profile.name.en.trim() !== '') {
        displayName = profile.name.en.trim();
      }
    }
    if (displayName === '') {
      displayName = profileId;
    }
    options.push({
      profileId: profileId,
      displayName: displayName
    });
  }
  return options;
});

const isBusyLoading = computed(() => {
  if (targetType.value === 'agents') {
    if (isLoadingAgents.value === true) {
      return true;
    }
    if (isLoadingAgent.value === true) {
      return true;
    }
    return false;
  }
  if (targetType.value === 'profiles') {
    if (isLoadingProfiles.value === true) {
      return true;
    }
    if (isLoadingEntities.value === true) {
      return true;
    }
    return false;
  }
  return false;
});

const saveDisabled = computed(() => {
  if (hasSelectedTarget.value === false) {
    return true;
  }
  if (isSaving.value === true) {
    return true;
  }
  if (targetType.value === 'agents' && isLoadingAgent.value === true) {
    return true;
  }
  if (targetType.value === 'profiles' && isLoadingProfiles.value === true) {
    return true;
  }
  return false;
});

const hasSelectedAgent = computed(() => {
  if (Array.isArray(selectedAgentIds.value) !== true) {
    return false;
  }
  if (selectedAgentIds.value.length === 0) {
    return false;
  }
  if (typeof selectedAgentIds.value[0] !== 'string') {
    return false;
  }
  if (selectedAgentIds.value[0].trim() === '') {
    return false;
  }
  return true;
});

const hasSelectedTarget = computed(() => {
  if (targetType.value === 'profiles') {
    if (normalizeProfileIdValue(selectedAnalysisProfileId.value) === '') {
      return false;
    }
    return true;
  }
  return hasSelectedAgent.value === true;
});

const runAnalysisLabel = computed(() => {
  if (targetType.value === 'profiles') {
    return t('analysisEngine.runAnalysisProfileLabel', 'Run analysis for entities attached to this profile');
  }
  return t('analysisEngine.runAnalysisLabel', 'Run analysis for this agent');
});

const selectTargetHint = computed(() => {
  if (targetType.value === 'profiles') {
    return t('analysisEngine.enableHintSelectProfile', 'Select or create an analysis profile to configure analysis.');
  }
  return t('analysisEngine.enableHintSelectAgent', 'Select an agent to enable analysis.');
});

const emptySelectTargetMessage = computed(() => {
  if (targetType.value === 'profiles') {
    return t('analysisEngine.emptySelectProfile', 'Select or create an analysis profile to configure the analysis engine.');
  }
  return t('analysisEngine.emptySelectAgent', 'Select an agent to configure the analysis engine.');
});

const customItemModalTitle = computed(() => {
  if (customItemEditIndex.value >= 0) {
    return t('analysisEngine.editCustomModalTitle', 'Edit custom analysis');
  }
  return t('analysisEngine.addCustomModalTitle', 'Add custom analysis');
});

const standardPromptModalTitle = computed(() => {
  const item = getStandardAnalysisItemByKey(standardPromptKey.value);
  let itemLabel = '';
  if (item !== null) {
    itemLabel = item.label;
  }
  if (itemLabel === '') {
    return t('analysisEngine.editSystemPromptButton', 'Edit analysis');
  }
  return t('analysisEngine.standardPromptModalTitle', { item: itemLabel }, 'Edit analysis: {item}');
});

function createLocalId() {
  if (window.crypto !== undefined && window.crypto !== null && typeof window.crypto.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }
  return `custom-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function normalizeKey(value) {
  if (typeof value !== 'string') {
    return '';
  }
  return value.trim().replace(/[^a-zA-Z0-9_-]/g, '').substring(0, 80);
}

function getSelectionKey(value) {
  if (typeof value === 'string') {
    return normalizeKey(value);
  }
  if (value === null) {
    return '';
  }
  if (value === undefined) {
    return '';
  }
  if (typeof value !== 'object') {
    return '';
  }
  const possibleKeys = ['key', 'itemKey', 'analysisKey', 'value', 'id', 'name'];
  for (let index = 0; index < possibleKeys.length; index = index + 1) {
    const possibleKey = possibleKeys[index];
    if (typeof value[possibleKey] === 'string') {
      const normalized = normalizeKey(value[possibleKey]);
      if (normalized !== '') {
        return normalized;
      }
    }
  }
  return '';
}

function getStandardAnalysisItemByKey(key) {
  for (let index = 0; index < standardAnalysisItems.value.length; index = index + 1) {
    if (standardAnalysisItems.value[index].key === key) {
      return standardAnalysisItems.value[index];
    }
  }
  return null;
}

function normalizeStandardPromptOverrides(value) {
  if (value === null) {
    return {};
  }
  if (value === undefined) {
    return {};
  }
  if (typeof value !== 'object') {
    return {};
  }
  if (Array.isArray(value) === true) {
    return {};
  }
  const normalized = {};
  const keys = Object.keys(value);
  for (let index = 0; index < keys.length; index = index + 1) {
    const key = normalizeKey(keys[index]);
    if (key === '') {
      continue;
    }
    const prompt = typeof value[keys[index]] === 'string' ? value[keys[index]].trim() : '';
    if (prompt !== '') {
      normalized[key] = prompt;
    }
  }
  return normalized;
}

function normalizeStandardDetailedExplanations(value) {
  if (value === null) {
    return {};
  }
  if (value === undefined) {
    return {};
  }
  if (typeof value !== 'object') {
    return {};
  }
  if (Array.isArray(value) === true) {
    return {};
  }
  const normalized = {};
  const keys = Object.keys(value);
  for (let index = 0; index < keys.length; index = index + 1) {
    const key = normalizeKey(keys[index]);
    if (key === '') {
      continue;
    }
    if (value[keys[index]] === true) {
      normalized[key] = true;
    }
  }
  return normalized;
}

function getStandardPromptOverride(key) {
  const normalizedKey = normalizeKey(key);
  if (normalizedKey === '') {
    return '';
  }
  const value = standardPromptOverrides.value[normalizedKey];
  if (typeof value !== 'string') {
    return '';
  }
  return value;
}

function hasStandardPromptOverride(key) {
  return getStandardPromptOverride(key).trim() !== '';
}

function isStandardDetailedExplanationEnabled(key) {
  const normalizedKey = normalizeKey(key);
  if (normalizedKey === '') {
    return false;
  }
  return standardDetailedExplanations.value[normalizedKey] === true;
}

function setStandardDetailedExplanation(key, event) {
  const normalizedKey = normalizeKey(key);
  if (normalizedKey === '') {
    return;
  }
  const checked = event !== null && event !== undefined && event.target !== null && event.target !== undefined && event.target.checked === true;
  const nextExplanations = { ...standardDetailedExplanations.value };
  if (checked === true) {
    nextExplanations[normalizedKey] = true;
  } else {
    delete nextExplanations[normalizedKey];
  }
  standardDetailedExplanations.value = nextExplanations;
}

function canEditStandardPrompt(item) {
  if (item === null) {
    return false;
  }
  if (item === undefined) {
    return false;
  }
  if (item.key === 'identifiedTags') {
    return false;
  }
  return true;
}

function openStandardPromptModal(item) {
  standardPromptKey.value = item.key;
  standardPromptDraft.value = getStandardPromptOverride(item.key);
  standardPromptModalOpen.value = true;
}

function closeStandardPromptModal() {
  standardPromptModalOpen.value = false;
}

function clearStandardPromptDraft() {
  standardPromptDraft.value = '';
}

async function saveStandardPromptFromModal() {
  const key = normalizeKey(standardPromptKey.value);
  if (key === '') {
    closeStandardPromptModal();
    return;
  }
  const nextPrompts = { ...standardPromptOverrides.value };
  const prompt = typeof standardPromptDraft.value === 'string' ? standardPromptDraft.value.trim() : '';
  if (prompt === '') {
    delete nextPrompts[key];
  } else {
    nextPrompts[key] = prompt;
  }
  standardPromptOverrides.value = nextPrompts;
  closeStandardPromptModal();
  await saveAnalysisEngine();
}

function getAuthToken() {
  const authTokenRow = document.cookie.split('; ').find(row => row.startsWith('authToken=') !== false);
  const jwtTokenRow = document.cookie.split('; ').find(row => row.startsWith('jwtToken=') !== false);
  const authToken = authTokenRow !== undefined ? authTokenRow.split('=')[1] : '';
  const jwtToken = jwtTokenRow !== undefined ? jwtTokenRow.split('=')[1] : '';
  if (authToken !== '') {
    return authToken;
  }
  return jwtToken;
}

function getAuthHeaders() {
  const token = getAuthToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token !== '') {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

function selectAllStandardItems() {
  const keys = [];
  for (let index = 0; index < standardAnalysisItems.value.length; index = index + 1) {
    keys.push(standardAnalysisItems.value[index].key);
  }
  selectedStandardItems.value = keys;
}

function clearStandardItems() {
  selectedStandardItems.value = [];
}

function toggleStandardItem(key) {
  if (engineEnabled.value === false) {
    return;
  }
  const index = selectedStandardItems.value.indexOf(key);
  if (index === -1) {
    selectedStandardItems.value = [...selectedStandardItems.value, key];
    return;
  }
  const nextItems = [];
  for (let itemIndex = 0; itemIndex < selectedStandardItems.value.length; itemIndex = itemIndex + 1) {
    if (itemIndex !== index) {
      nextItems.push(selectedStandardItems.value[itemIndex]);
    }
  }
  selectedStandardItems.value = nextItems;
}

function normalizeCustomItems(items) {
  const sourceItems = Array.isArray(items) === true ? items : [];
  const normalized = [];
  for (let index = 0; index < sourceItems.length; index = index + 1) {
    const item = sourceItems[index];
    if (item === null) {
      continue;
    }
    if (item === undefined) {
      continue;
    }
    if (typeof item !== 'object') {
      continue;
    }
    normalized.push({
      localId: createLocalId(),
      key: typeof item.key === 'string' && item.key.trim() !== '' ? item.key.trim() : `custom${index + 1}`,
      name: typeof item.name === 'string' && item.name.trim() !== '' ? item.name.trim() : (typeof item.key === 'string' ? item.key.trim() : `Custom ${index + 1}`),
      prompt: typeof item.prompt === 'string' ? item.prompt : '',
      outputType: typeof item.outputType === 'string' && item.outputType.trim() !== '' ? item.outputType.trim() : 'text',
      includeDetailedExplanation: item.includeDetailedExplanation === true
    });
  }
  return normalized;
}

function getAgentData(agent) {
  if (agent === null) {
    return {};
  }
  if (agent === undefined) {
    return {};
  }
  if (agent.agentdata !== null && agent.agentdata !== undefined && typeof agent.agentdata === 'object') {
    return agent.agentdata;
  }
  if (agent.agentData !== null && agent.agentData !== undefined && typeof agent.agentData === 'object') {
    return agent.agentData;
  }
  return {};
}

function applyEngineFromConfig(engine) {
  const safeEngine = engine !== undefined && engine !== null && typeof engine === 'object' ? engine : {};
  engineEnabled.value = safeEngine.enabled !== false;
  let hasStoredSelection = false;
  let selected = [];
  if (Array.isArray(safeEngine.selectedItems) === true) {
    selected = safeEngine.selectedItems;
    hasStoredSelection = true;
  } else {
    if (Array.isArray(safeEngine.selectedStandardItems) === true) {
      selected = safeEngine.selectedStandardItems;
      hasStoredSelection = true;
    }
  }
  if (hasStoredSelection === true) {
    const nextItems = [];
    for (let index = 0; index < selected.length; index = index + 1) {
      const key = getSelectionKey(selected[index]);
      if (key !== '') {
        nextItems.push(key);
      }
    }
    selectedStandardItems.value = nextItems;
  } else {
    selectAllStandardItems();
  }
  customItems.value = normalizeCustomItems(safeEngine.customItems);
  let promptOverridesSource = safeEngine.standardPrompts;
  if (promptOverridesSource === undefined) {
    promptOverridesSource = safeEngine.standardPromptOverrides;
  }
  standardPromptOverrides.value = normalizeStandardPromptOverrides(promptOverridesSource);
  let detailedExplanationsSource = safeEngine.standardDetailedExplanations;
  if (detailedExplanationsSource === undefined) {
    detailedExplanationsSource = safeEngine.standardExplanations;
  }
  if (detailedExplanationsSource === undefined) {
    detailedExplanationsSource = safeEngine.detailedExplanations;
  }
  standardDetailedExplanations.value = normalizeStandardDetailedExplanations(detailedExplanationsSource);
}

function getAnalysisProfileById(profileId) {
  const normalizedProfileId = normalizeProfileIdValue(profileId);
  if (normalizedProfileId === '') {
    return null;
  }
  const profiles = Array.isArray(analysisProfiles.value) === true ? analysisProfiles.value : [];
  for (let index = 0; index < profiles.length; index = index + 1) {
    const profile = profiles[index];
    if (profile !== null && profile !== undefined && typeof profile === 'object' && normalizeProfileIdValue(profile.profileId) === normalizedProfileId) {
      return profile;
    }
  }
  return null;
}

function getAnalysisProfileName(profile) {
  if (profile === null) {
    return '';
  }
  if (profile === undefined) {
    return '';
  }
  if (typeof profile !== 'object') {
    return '';
  }
  const languageCode = currentLanguage.value === 'he' ? 'he' : 'en';
  if (profile.name !== undefined && profile.name !== null && typeof profile.name === 'object') {
    if (typeof profile.name[languageCode] === 'string' && profile.name[languageCode].trim() !== '') {
      return profile.name[languageCode].trim();
    }
    if (typeof profile.name.en === 'string' && profile.name.en.trim() !== '') {
      return profile.name.en.trim();
    }
  }
  return normalizeProfileIdValue(profile.profileId);
}

function applyProfileEntitySelection(profileId) {
  const normalizedProfileId = normalizeProfileIdValue(profileId);
  const nextEntityIds = [];
  const sourceEntities = Array.isArray(entities.value) === true ? entities.value : [];
  for (let index = 0; index < sourceEntities.length; index = index + 1) {
    const entity = sourceEntities[index];
    if (entity === null) {
      continue;
    }
    if (entity === undefined) {
      continue;
    }
    if (typeof entity !== 'object') {
      continue;
    }
    const entityId = typeof entity.entityId === 'string' ? entity.entityId.trim() : '';
    const entityProfileId = normalizeProfileIdValue(entity.analysisProfileId);
    if (entityId !== '' && normalizedProfileId !== '' && entityProfileId === normalizedProfileId) {
      nextEntityIds.push(entityId);
    }
  }
  profileEntityIds.value = nextEntityIds;
}

function applyEngineFromProfile(profile) {
  if (profile === null) {
    return;
  }
  if (profile === undefined) {
    return;
  }
  if (typeof profile !== 'object') {
    return;
  }
  analysisProfileNameDraft.value = getAnalysisProfileName(profile);
  const engine = profile.analysisEngine !== undefined && profile.analysisEngine !== null && typeof profile.analysisEngine === 'object' ? profile.analysisEngine : {};
  applyEngineFromConfig(engine);
  syncSelectedAnalysisCollectionIds(profile.selectedAnalysisCollectionIds);
  applyProfileEntitySelection(profile.profileId);
}

function applyEngineFromAgent(agent) {
  selectedAgent.value = agent;
  const engine = agent !== null && agent !== undefined && agent.analysisEngine !== undefined && agent.analysisEngine !== null && typeof agent.analysisEngine === 'object'
    ? agent.analysisEngine
    : {};
  applyEngineFromConfig(engine);
}

async function loadAgents() {
  isLoadingAgents.value = true;
  try {
    const data = await getAllAIChats({ includeStyles: false, force: true });
    agents.value = Array.isArray(data) === true ? data : [];
  } catch (error) {
    toast.error(t('analysisEngine.messages.loadAgentsError', 'Failed to load agents.'));
    agents.value = [];
  } finally {
    isLoadingAgents.value = false;
  }
}

async function loadEntities() {
  isLoadingEntities.value = true;
  try {
    const response = await axios.get(`${globalState.apiEndpoint}/api/entitiesAnalysis/entities`, { headers: getAuthHeaders() });
    const list = response.data !== undefined && response.data !== null && Array.isArray(response.data.entities) === true ? response.data.entities : [];
    entities.value = list;
  } catch (error) {
    toast.error(t('analysisEngine.messages.loadEntitiesError', 'Failed to load entities.'));
    entities.value = [];
  } finally {
    isLoadingEntities.value = false;
  }
}

async function loadAnalysisProfiles() {
  isLoadingProfiles.value = true;
  try {
    const response = await axios.get(`${globalState.apiEndpoint}/api/entitiesAnalysis/analysisProfiles`, { headers: getAuthHeaders() });
    const list = response.data !== undefined && response.data !== null && Array.isArray(response.data.profiles) === true ? response.data.profiles : [];
    analysisProfiles.value = list;
  } catch (error) {
    toast.error(t('analysisEngine.messages.loadAnalysisProfilesError', 'Failed to load analysis profiles.'));
    analysisProfiles.value = [];
  } finally {
    isLoadingProfiles.value = false;
  }
}

async function loadAnalysisCollections() {
  isLoadingAnalysisCollections.value = true;
  try {
    const response = await axios.get(`${globalState.apiEndpoint}/api/tags/collections`, { headers: getAuthHeaders() });
    const responseRows = Array.isArray(response.data) === true
      ? response.data
      : (response.data !== undefined && response.data !== null && Array.isArray(response.data.collections) === true ? response.data.collections : []);
    const normalizedCollections = [];
    for (let index = 0; index < responseRows.length; index = index + 1) {
      const normalizedCollection = normalizeAnalysisCollectionRow(responseRows[index]);
      if (normalizedCollection === null) {
        continue;
      }
      if (normalizedCollections.some((item) => String(item.collectionId) === String(normalizedCollection.collectionId)) === true) {
        continue;
      }
      normalizedCollections.push(normalizedCollection);
    }
    availableAnalysisCollections.value = normalizedCollections;
    syncSelectedAnalysisCollectionIds(selectedAnalysisCollectionIds.value);
  } catch (error) {
    toast.error(t('analysisEngine.messages.loadCollectionsError', 'Failed to load tag collections.'));
    availableAnalysisCollections.value = [];
  } finally {
    isLoadingAnalysisCollections.value = false;
  }
}

async function loadAgent(agentId) {
  if (typeof agentId !== 'string') {
    selectedAgent.value = null;
    return;
  }
  if (agentId.trim() === '') {
    selectedAgent.value = null;
    return;
  }
  isLoadingAgent.value = true;
  try {
    const response = await axios.get(`${globalState.apiEndpoint}/getAIChat/${encodeURIComponent(agentId.trim())}`, { headers: getAuthHeaders() });
    applyEngineFromAgent(response.data);
  } catch (error) {
    toast.error(t('analysisEngine.messages.loadEngineError', 'Failed to load analysis engine.'));
    selectedAgent.value = null;
  } finally {
    isLoadingAgent.value = false;
  }
}

function openAddCustomItemModal() {
  customItemEditIndex.value = -1;
  customItemDraft.value = {
    key: `custom${customItems.value.length + 1}`,
    name: '',
    prompt: '',
    outputType: 'text',
    includeDetailedExplanation: false
  };
  customItemModalOpen.value = true;
}

function openEditCustomItemModal(index) {
  if (index < 0) {
    return;
  }
  if (index >= customItems.value.length) {
    return;
  }
  const item = customItems.value[index];
  customItemEditIndex.value = index;
  customItemDraft.value = {
    key: typeof item.key === 'string' ? item.key : '',
    name: typeof item.name === 'string' ? item.name : '',
    prompt: typeof item.prompt === 'string' ? item.prompt : '',
    outputType: typeof item.outputType === 'string' ? item.outputType : 'text',
    includeDetailedExplanation: item.includeDetailedExplanation === true
  };
  customItemModalOpen.value = true;
}

function closeCustomItemModal() {
  customItemModalOpen.value = false;
}

async function saveCustomItemFromModal() {
  const prompt = typeof customItemDraft.value.prompt === 'string' ? customItemDraft.value.prompt.trim() : '';
  if (prompt === '') {
    toast.error(t('analysisEngine.messages.promptRequired', 'Prompt is required.'));
    return;
  }
  let key = normalizeKey(customItemDraft.value.key);
  if (key === '') {
    key = `custom${customItems.value.length + 1}`;
  }
  const item = {
    localId: createLocalId(),
    key: key,
    name: typeof customItemDraft.value.name === 'string' ? customItemDraft.value.name.trim() : '',
    prompt: prompt,
    outputType: typeof customItemDraft.value.outputType === 'string' && customItemDraft.value.outputType.trim() !== '' ? customItemDraft.value.outputType.trim() : 'text',
    includeDetailedExplanation: customItemDraft.value.includeDetailedExplanation === true
  };
  if (customItemEditIndex.value >= 0) {
    const nextItems = [...customItems.value];
    item.localId = nextItems[customItemEditIndex.value].localId;
    nextItems[customItemEditIndex.value] = item;
    customItems.value = nextItems;
  } else {
    customItems.value.push(item);
  }
  closeCustomItemModal();
  await saveAnalysisEngine();
}

function getOutputTypeLabel(value) {
  if (value === 'boolean') {
    return t('analysisEngine.outputTypes.boolean', 'Boolean');
  }
  if (value === 'number') {
    return t('analysisEngine.outputTypes.number', 'Number');
  }
  if (value === 'object') {
    return t('analysisEngine.outputTypes.object', 'Object');
  }
  if (value === 'array') {
    return t('analysisEngine.outputTypes.array', 'Array');
  }
  return t('analysisEngine.outputTypes.text', 'Text');
}

function getStandardProcessingTooltipTitle(item) {
  const label = item !== null && item !== undefined && typeof item.label === 'string' ? item.label : '';
  return t('analysisEngine.processingTooltip.standardTitle', { item: label }, 'How {item} is analyzed');
}

function getStandardProcessingTooltipBody(item) {
  const description = item !== null && item !== undefined && typeof item.description === 'string' ? item.description : '';
  const itemKey = item !== null && item !== undefined && typeof item.key === 'string' ? item.key : '';
  if (itemKey !== '') {
    return t(`analysisEngine.standardItems.${itemKey}.info`, description);
  }
  return description;
}

function getCustomProcessingTooltipTitle(item) {
  const label = item !== null && item !== undefined && typeof item.name === 'string' && item.name.trim() !== '' ? item.name.trim() : (item !== null && item !== undefined && typeof item.key === 'string' ? item.key : '');
  return t('analysisEngine.processingTooltip.customTitle', { item: label }, 'How {item} is analyzed');
}

function getCustomProcessingTooltipBody(item) {
  const outputType = item !== null && item !== undefined ? getOutputTypeLabel(item.outputType) : getOutputTypeLabel('text');
  return t(
    'analysisEngine.processingTooltip.customBody',
    { output: outputType },
    'We read the whole conversation and its context, apply your custom analysis instruction, and produce a value with a short explanation. Use it for business checks such as risk, quality, intent, urgency, or follow-up needs. Expected result type: {output}.'
  );
}

async function removeCustomItem(index) {
  const nextItems = [];
  for (let itemIndex = 0; itemIndex < customItems.value.length; itemIndex = itemIndex + 1) {
    if (itemIndex !== index) {
      nextItems.push(customItems.value[itemIndex]);
    }
  }
  customItems.value = nextItems;
  await saveAnalysisEngine();
}

function buildCleanCustomItems() {
  const items = [];
  const usedKeys = new Set();
  for (let index = 0; index < customItems.value.length; index = index + 1) {
    const item = customItems.value[index];
    const prompt = typeof item.prompt === 'string' ? item.prompt.trim() : '';
    if (prompt === '') {
      continue;
    }
    let key = normalizeKey(item.key);
    if (key === '') {
      key = `custom${index + 1}`;
    }
    let uniqueKey = key;
    let suffix = 2;
    while (usedKeys.has(uniqueKey.toLowerCase()) === true) {
      uniqueKey = `${key}${suffix}`;
      suffix = suffix + 1;
    }
    usedKeys.add(uniqueKey.toLowerCase());
    const name = typeof item.name === 'string' && item.name.trim() !== '' ? item.name.trim() : uniqueKey;
    items.push({
      key: uniqueKey,
      name: name,
      prompt: prompt,
      outputType: typeof item.outputType === 'string' && item.outputType.trim() !== '' ? item.outputType.trim() : 'text',
      includeDetailedExplanation: item.includeDetailedExplanation === true
    });
  }
  return items;
}

function buildCleanStandardPrompts() {
  return normalizeStandardPromptOverrides(standardPromptOverrides.value);
}

function buildCleanStandardDetailedExplanations() {
  const source = normalizeStandardDetailedExplanations(standardDetailedExplanations.value);
  const selected = Array.isArray(selectedStandardItems.value) === true ? selectedStandardItems.value : [];
  const selectedSet = new Set();
  for (let index = 0; index < selected.length; index = index + 1) {
    const key = normalizeKey(selected[index]);
    if (key !== '') {
      selectedSet.add(key);
    }
  }
  const cleaned = {};
  const keys = Object.keys(source);
  for (let index = 0; index < keys.length; index = index + 1) {
    const key = keys[index];
    if (selectedSet.has(key) === true) {
      cleaned[key] = true;
    }
  }
  return cleaned;
}

const analysisEnginePayload = computed(() => ({
  enabled: engineEnabled.value,
  selectedItems: selectedStandardItems.value,
  standardPrompts: buildCleanStandardPrompts(),
  standardDetailedExplanations: buildCleanStandardDetailedExplanations(),
  customItems: buildCleanCustomItems()
}));

async function attachAnalysisProfileToEntityId(entityId, profileId) {
  const safeEntityId = typeof entityId === 'string' ? entityId.trim() : '';
  if (safeEntityId === '') {
    return null;
  }
  const response = await axios.put(`${globalState.apiEndpoint}/api/entitiesAnalysis/entities/${encodeURIComponent(safeEntityId)}/analysisProfile`, {
    analysisProfileId: normalizeProfileIdValue(profileId)
  }, { headers: getAuthHeaders() });
  if (response.data !== undefined && response.data !== null && response.data.entity !== undefined && response.data.entity !== null) {
    return response.data.entity;
  }
  return null;
}

function openCreateProfileModal() {
  newAnalysisProfileNameDraft.value = '';
  createProfileModalOpen.value = true;
}

function closeCreateProfileModal() {
  if (isSaving.value === true) {
    return;
  }
  createProfileModalOpen.value = false;
  newAnalysisProfileNameDraft.value = '';
}

async function createAnalysisProfile() {
  const profileName = typeof newAnalysisProfileNameDraft.value === 'string' ? newAnalysisProfileNameDraft.value.trim() : '';
  if (profileName === '') {
    toast.error(t('analysisEngine.messages.profileNameRequired', 'Profile name is required.'));
    return;
  }
  isSaving.value = true;
  try {
    const languageCode = currentLanguage.value === 'he' ? 'he' : 'en';
    const selectedCollectionIds = normalizeAnalysisCollectionIds(selectedAnalysisCollectionIds.value);
    const response = await axios.post(`${globalState.apiEndpoint}/api/entitiesAnalysis/analysisProfiles`, {
      name: { [languageCode]: profileName, en: profileName },
      analysisEngine: analysisEnginePayload.value,
      selectedAnalysisCollectionIds: selectedCollectionIds
    }, { headers: getAuthHeaders() });
    const profile = response.data !== undefined && response.data !== null ? response.data.profile : null;
    if (profile === null || profile === undefined || normalizeProfileIdValue(profile.profileId) === '') {
      toast.error(t('analysisEngine.messages.saveError', 'Failed to save analysis engine.'));
      return;
    }
    await loadAnalysisProfiles();
    isApplyingAnalysisProfile.value = true;
    selectedAnalysisProfileId.value = profile.profileId;
    window.setTimeout(() => {
      isApplyingAnalysisProfile.value = false;
    }, 0);
    applyEngineFromProfile(profile);
    createProfileModalOpen.value = false;
    newAnalysisProfileNameDraft.value = '';
    toast.success(t('analysisEngine.messages.saveSuccess', 'Analysis engine saved.'));
  } catch (error) {
    toast.error(t('analysisEngine.messages.saveError', 'Failed to save analysis engine.'));
  } finally {
    isSaving.value = false;
  }
}

async function saveAnalysisEngine() {
  if (targetType.value === 'profiles') {
    const profileId = normalizeProfileIdValue(selectedAnalysisProfileId.value);
    if (profileId === '') {
      toast.error(t('analysisEngine.messages.selectProfileFirst', 'Select an analysis profile first.'));
      return;
    }
    const profile = getAnalysisProfileById(profileId);
    const profileNameDraft = typeof analysisProfileNameDraft.value === 'string' ? analysisProfileNameDraft.value.trim() : '';
    let profileName = profile !== null ? profile.name : { en: profileId };
    if (profileNameDraft !== '') {
      const languageCode = currentLanguage.value === 'he' ? 'he' : 'en';
      profileName = { ...profileName, [languageCode]: profileNameDraft, en: profileNameDraft };
    }
    isSaving.value = true;
    try {
      const selectedCollectionIds = normalizeAnalysisCollectionIds(selectedAnalysisCollectionIds.value);
      await axios.put(`${globalState.apiEndpoint}/api/entitiesAnalysis/analysisProfiles/${encodeURIComponent(profileId)}`, {
        name: profileName,
        analysisEngine: analysisEnginePayload.value,
        selectedAnalysisCollectionIds: selectedCollectionIds
      }, { headers: getAuthHeaders() });
      const selectedIds = Array.isArray(profileEntityIds.value) === true ? profileEntityIds.value : [];
      const sourceEntities = Array.isArray(entities.value) === true ? entities.value : [];
      for (let index = 0; index < sourceEntities.length; index = index + 1) {
        const entity = sourceEntities[index];
        if (entity === null) {
          continue;
        }
        if (entity === undefined) {
          continue;
        }
        if (typeof entity !== 'object') {
          continue;
        }
        const entityId = typeof entity.entityId === 'string' ? entity.entityId.trim() : '';
        if (entityId === '') {
          continue;
        }
        const entityProfileId = normalizeProfileIdValue(entity.analysisProfileId);
        const shouldAttach = selectedIds.includes(entityId) === true;
        if (shouldAttach === true && entityProfileId !== profileId) {
          await attachAnalysisProfileToEntityId(entityId, profileId);
        }
        if (shouldAttach === false && entityProfileId === profileId) {
          await attachAnalysisProfileToEntityId(entityId, '');
        }
      }
      toast.success(t('analysisEngine.messages.saveSuccess', 'Analysis engine saved.'));
      await loadAnalysisProfiles();
      await loadEntities();
      const refreshedProfile = getAnalysisProfileById(profileId);
      if (refreshedProfile !== null) {
        applyEngineFromProfile(refreshedProfile);
      } else {
        applyProfileEntitySelection(profileId);
      }
    } catch (error) {
      toast.error(t('analysisEngine.messages.saveError', 'Failed to save analysis engine.'));
    } finally {
      isSaving.value = false;
    }
    return;
  }
  const agent = selectedAgent.value;
  if (agent === null) {
    toast.error(t('analysisEngine.messages.selectAgentFirst', 'Select an agent first.'));
    return;
  }
  if (agent === undefined) {
    toast.error(t('analysisEngine.messages.selectAgentFirst', 'Select an agent first.'));
    return;
  }
  const agentId = typeof agent.agentid === 'string' ? agent.agentid.trim() : '';
  if (agentId === '') {
    toast.error(t('analysisEngine.messages.selectAgentFirst', 'Select an agent first.'));
    return;
  }
  isSaving.value = true;
  try {
    let latestAgentData = getAgentData(agent);
    try {
      const refreshResponse = await axios.get(`${globalState.apiEndpoint}/getAIChat/${encodeURIComponent(agentId)}`, { headers: getAuthHeaders() });
      if (refreshResponse.data !== null && refreshResponse.data !== undefined) {
        latestAgentData = getAgentData(refreshResponse.data);
      }
    } catch (refreshError) {
      latestAgentData = getAgentData(agent);
    }
    const agentData = {
      ...latestAgentData
    };
    delete agentData.analysisEngine;
    const response = await axios.put(`${globalState.apiEndpoint}/modifyAIChat/${encodeURIComponent(agentId)}`, {
      aiAgentName: agent.agentname,
      aiAgentStatus: agent.agentstatus,
      agentShortDescription: agent.agentShortDescription !== undefined ? agent.agentShortDescription : '',
      analysisEngine: analysisEnginePayload.value,
      agentData: agentData,
      agentType: agent.agenttype,
      agentMode: agent.agentMode,
      divisionIds: Array.isArray(agent.divisionIds) === true ? agent.divisionIds : []
    }, { headers: getAuthHeaders() });
    invalidateAllAIChatsCache();
    if (response.data !== null && response.data !== undefined) {
      selectedAgent.value = {
        ...agent,
        ...response.data,
        agentdata: agentData,
        agentData: agentData,
        analysisEngine: analysisEnginePayload.value
      };
    }
    toast.success(t('analysisEngine.messages.saveSuccess', 'Analysis engine saved.'));
    await loadAgent(agentId);
  } catch (error) {
    toast.error(t('analysisEngine.messages.saveError', 'Failed to save analysis engine.'));
  } finally {
    isSaving.value = false;
  }
}

watch(selectedAgentIds, async (value) => {
  if (targetType.value !== 'agents') {
    return;
  }
  if (Array.isArray(value) !== true) {
    selectedAgent.value = null;
    return;
  }
  if (value.length === 0) {
    selectedAgent.value = null;
    return;
  }
  await loadAgent(String(value[0]));
});

watch(selectedAnalysisProfileId, async (value) => {
  if (targetType.value !== 'profiles') {
    return;
  }
  if (isApplyingAnalysisProfile.value === true) {
    return;
  }
  const profileId = normalizeProfileIdValue(value);
  if (profileId !== '') {
    const profile = getAnalysisProfileById(profileId);
    if (profile !== null) {
      applyEngineFromProfile(profile);
    }
  } else {
    analysisProfileNameDraft.value = '';
    profileEntityIds.value = [];
    selectedAnalysisCollectionIds.value = [];
  }
});

watch(targetType, async (value) => {
  if (value === 'profiles') {
    selectedAgent.value = null;
    selectedAgentIds.value = [];
    if (entities.value.length === 0) {
      await loadEntities();
    }
    if (analysisProfiles.value.length === 0) {
      await loadAnalysisProfiles();
    }
    if (availableAnalysisCollections.value.length === 0) {
      await loadAnalysisCollections();
    }
    return;
  }
  selectedAnalysisProfileId.value = '';
  profileEntityIds.value = [];
  selectedAnalysisCollectionIds.value = [];
  if (agents.value.length === 0) {
    await loadAgents();
  }
});

function applyRouteTargetSelection() {
  const routeTarget = typeof route.query?.target === 'string' ? route.query.target.trim().toLowerCase() : '';
  const routeProfileId = typeof route.query?.profileId === 'string' ? route.query.profileId.trim() : '';
  if (routeTarget === 'profiles') {
    targetType.value = 'profiles';
    if (routeProfileId !== '') {
      selectedAnalysisProfileId.value = routeProfileId;
    }
    return;
  }
  if (routeTarget === 'agents') {
    targetType.value = 'agents';
  }
}

onMounted(async () => {
  applyRouteTargetSelection();
  await loadAgents();
  await loadEntities();
  await loadAnalysisProfiles();
  await loadAnalysisCollections();
  if (targetType.value === 'profiles' && normalizeProfileIdValue(selectedAnalysisProfileId.value) !== '') {
    const profile = getAnalysisProfileById(selectedAnalysisProfileId.value);
    if (profile !== null) {
      applyEngineFromProfile(profile);
    }
  }
});
</script>

<style scoped>
.analysis-engine-page {
  width: calc(100% - 1.5rem);
  max-width: 100%;
  box-sizing: border-box;
  margin: 1.5rem auto 0 auto;
}

.analysis-engine-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0.5rem 0.75rem 1rem 0.75rem;
}

.analysis-engine-page-rtl {
  text-align: right;
}

.analysis-engine-page-rtl .analysis-engine-header-intro {
  text-align: right;
}

.analysis-save-spinner {
  margin-inline-end: 0.5rem;
}

.analysis-card {
  margin: 0 0.75rem 1.5rem 0.75rem;
}

.analysis-controls {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  margin: -0.5rem;
}

.analysis-controls > * {
  margin: 0.5rem;
}

.analysis-target-type-select {
  min-width: 12rem;
  flex: 0 1 14rem;
}

.analysis-agent-select {
  min-width: 18rem;
  flex: 1 1 20rem;
}

.analysis-profile-select {
  min-width: 16rem;
  flex: 0 1 18rem;
}

.analysis-profile-create {
  min-width: 10rem;
  flex: 0 0 auto;
}

.analysis-profile-create-button {
  min-height: 3rem;
}

.analysis-enable-panel {
  margin-top: 0.25rem;
  margin-bottom: 0.5rem;
  padding: 0.875rem 1rem;
  border: 1px solid rgba(229, 231, 235, 0.95);
  border-radius: 0.75rem;
  background: rgba(249, 250, 251, 0.9);
}

.analysis-engine-page-rtl .analysis-enable-panel {
  text-align: right;
}

.analysis-enable-panel-disabled {
  opacity: 0.65;
}

.analysis-enable-panel-off {
  border-color: rgba(209, 213, 219, 0.95);
  background: rgba(243, 244, 246, 0.75);
}

.analysis-engine-toggle {
  width: 100%;
  padding-top: 0;
  padding-bottom: 0;
  min-height: 2.75rem;
}

.analysis-engine-toggle-text {
  flex: 1 1 auto;
  min-width: 0;
  font-weight: 600;
}

.analysis-engine-page-rtl .analysis-engine-toggle-text {
  text-align: right;
}

.analysis-enable-hint {
  margin-top: 0.5rem;
  font-size: 0.8125rem;
  line-height: 1.35;
  color: #6b7280;
}

.analysis-engine-page-rtl .analysis-enable-hint {
  text-align: right;
}

.analysis-loader,
.analysis-empty {
  min-height: 16rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
}

.analysis-section {
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(229, 231, 235, 0.9);
}

.analysis-section-title-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
}

.analysis-section-heading {
  flex: 1 1 auto;
  min-width: 0;
}

.analysis-engine-page-rtl .analysis-section-heading {
  text-align: right;
}

.analysis-section-actions {
  flex-shrink: 0;
}

.analysis-engine-page-rtl .analysis-section-actions {
  margin-inline-start: 1rem;
}

.analysis-engine-page-rtl .analysis-empty-small {
  text-align: right;
}

.analysis-buttons-row {
  display: flex;
  flex-wrap: wrap;
  margin: -0.25rem;
}

.analysis-buttons-row > * {
  margin: 0.25rem;
}

.analysis-items-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(17rem, 1fr));
  margin: -0.5rem;
}

.analysis-item-card {
  position: relative;
  display: flex;
  align-items: flex-start;
  padding: 1rem;
  margin: 0.5rem;
  border: 1px solid rgba(229, 231, 235, 0.9);
  border-radius: 0.75rem;
  background: rgba(249, 250, 251, 0.85);
}

.analysis-item-checkbox {
  flex-shrink: 0;
  margin-top: 0.125rem;
}

.analysis-item-copy {
  flex: 1 1 auto;
  min-width: 0;
  margin-inline-start: 0.75rem;
  text-align: start;
}

.analysis-item-title-row {
  min-height: 2.35rem;
}

.analysis-engine-page-rtl .analysis-item-copy,
.analysis-engine-page-rtl .analysis-item-title-row {
  text-align: right;
}

.analysis-engine-page-rtl .analysis-item-title-row {
  justify-content: flex-start;
}

.analysis-item-title-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin: -0.125rem;
}

.analysis-item-title-row > * {
  margin: 0.125rem;
}

.analysis-standard-prompt-row {
  min-height: 1.75rem;
  display: flex;
  align-items: center;
  margin-top: 0.15rem;
}

.analysis-standard-prompt-edit {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  min-height: 1.55rem;
  border: 1px solid rgba(14, 165, 233, 0.45);
  border-radius: 999px;
  background: rgba(236, 254, 255, 0.85);
  color: #0e7490;
  padding: 0.15rem 0.5rem;
  font-size: 0.72rem;
  font-weight: 600;
  line-height: 1;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease, opacity 0.15s ease;
}

.analysis-standard-prompt-edit > svg {
  margin-inline-end: 0.25rem;
}

.analysis-standard-prompt-edit:hover:not(:disabled) {
  border-color: rgba(8, 145, 178, 0.9);
  background: rgba(207, 250, 254, 0.95);
  color: #155e75;
}

.analysis-standard-prompt-edit:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.analysis-standard-prompt-placeholder {
  display: inline-block;
  min-height: 1.55rem;
}

.analysis-detailed-explanation-toggle {
  display: inline-flex;
  align-items: center;
  margin-top: 0.35rem;
  color: #64748b;
  font-size: 0.72rem;
  font-weight: 600;
}

.analysis-detailed-explanation-toggle > span {
  margin-inline-start: 0.4rem;
}

.analysis-detailed-explanation-status {
  display: inline-flex;
  align-items: center;
  margin-top: 0.35rem;
  color: #0f766e;
  font-size: 0.72rem;
  font-weight: 600;
}

.analysis-detailed-explanation-status > svg {
  margin-inline-end: 0.25rem;
}

.analysis-detailed-explanation-modal-toggle {
  display: flex;
  align-items: flex-start;
  margin-top: 1rem;
  padding: 0.75rem;
  border: 1px solid rgba(20, 184, 166, 0.35);
  border-radius: 0.75rem;
  background: rgba(240, 253, 250, 0.75);
}

.analysis-detailed-explanation-modal-toggle > input {
  flex-shrink: 0;
  margin-top: 0.15rem;
}

.analysis-detailed-explanation-modal-toggle > span {
  display: flex;
  flex-direction: column;
  margin-inline-start: 0.75rem;
}

.analysis-detailed-explanation-modal-toggle small {
  margin-top: 0.2rem;
  color: #64748b;
  line-height: 1.35;
}

.analysis-item-tooltip-shell {
  flex-shrink: 0;
  margin-inline-start: 0.5rem;
}

.analysis-item-tooltip :deep(.btn) {
  width: 2rem;
  height: 2rem;
  min-height: 2rem;
}

.analysis-item-tooltip :deep(svg) {
  width: 1.45rem;
  height: 1.45rem;
}

.analysis-item-tooltip :deep(.dropdown-content) {
  z-index: 50;
}

.custom-analysis-title-row {
  display: flex;
  align-items: center;
  margin: -0.25rem;
}

.custom-analysis-title-row > * {
  margin: 0.25rem;
}

.custom-analysis-item {
  margin-top: 0.75rem;
}

.custom-analysis-display-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;
  margin: -0.25rem;
}

.custom-analysis-display-row > * {
  margin: 0.25rem;
}

.custom-analysis-display-copy {
  flex: 1 1 auto;
  min-width: 0;
}

.analysis-engine-page-rtl .custom-analysis-display-copy,
.analysis-engine-page-rtl .custom-analysis-item > p {
  text-align: right;
}

.analysis-engine-page-rtl .custom-analysis-display-row .analysis-buttons-row {
  justify-content: flex-start;
}

.custom-analysis-modal-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(12rem, 1fr));
  margin: -0.5rem;
}

.custom-analysis-modal-grid > * {
  margin: 0.5rem;
}

.analysis-modal-content-rtl {
  text-align: right;
}

.analysis-modal-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  margin: -0.25rem;
}

.analysis-modal-actions > * {
  margin: 0.25rem;
}

.analysis-modal-actions-rtl {
  justify-content: flex-start;
}

.analysis-empty-small {
  border: 1px dashed rgba(156, 163, 175, 0.75);
  border-radius: 0.75rem;
  padding: 1rem;
  color: #6b7280;
}

@media (max-width: 768px) {
  .analysis-engine-header,
  .analysis-section-title-row {
    align-items: stretch;
    flex-direction: column;
  }

  .analysis-section-title-row > .analysis-section-actions,
  .analysis-section-title-row > .analysis-buttons-row {
    margin-top: 0.75rem;
  }

  .analysis-engine-page-rtl .analysis-section-title-row > .analysis-section-actions,
  .analysis-engine-page-rtl .analysis-section-title-row > .analysis-buttons-row {
    align-self: flex-start;
  }
}
</style>
